import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { msg } from "@lingui/core/macro"
import { Trans, useLingui } from "@lingui/react/macro"
import type { DealDrawer as DealDrawerData } from "@/types/v2/conversation"
import type { DealPropKey } from "@/hooks/use-stage-move"
import {
  useDealUpdate,
  formatCurrencyDisplay,
  formatCloseAtDisplay,
} from "@/hooks/use-deal-update"
import { StagePicker } from "@/components/v2/deals/stage-picker"
import {
  DatePopover,
  addDays,
  endOfMonth,
  isoFromDate,
  type DatePopoverPreset,
} from "@/components/v2/inline-edit/date-popover"
import {
  AvatarStack,
  stackLabel,
} from "@/components/v2/assignees/avatar-stack"
import {
  DealOwnersPicker,
  dealOwnersList,
} from "@/components/v2/deals/deal-owners-picker"


// The Properties card on the desktop deal slideover and
// the deals-index drawer. Stage is a popover; Value,
// Close date, and Owner are inline editors with the
// "warm tint + orange stripe" affordance per Paper Q1L-1.
// Source and Created stay read-only (AI / system).

interface DealPropertiesCardProps {
  deal: DealDrawerData
  dealPropKey: DealPropKey
}

export function DealPropertiesCard({
  deal,
  dealPropKey,
}: DealPropertiesCardProps) {
  const { t } = useLingui()

  return (
    <section className="flex flex-col gap-2.5">
      <SectionLabel><Trans>Properties</Trans></SectionLabel>
      <div className="flex flex-col rounded-xl overflow-clip bg-card border border-[#E5DFD3]">
        <PropertyRow label={t`Stage`}>
          <StagePicker deal={deal} dealPropKey={dealPropKey} />
        </PropertyRow>
        <Divider />
        <ValueRow deal={deal} dealPropKey={dealPropKey} />
        <Divider />
        <CloseDateRow deal={deal} dealPropKey={dealPropKey} />
        <Divider />
        <OwnerRow deal={deal} dealPropKey={dealPropKey} />
        <Divider />
        <PropertyRow label={t`Source`}>
          {deal.source ? (
            <div className="flex items-center gap-1.5">
              {deal.source.kind === "whatsapp" ? (
                <WhatsAppMark />
              ) : (
                <span className="size-1.5 rounded-full bg-[#9A938A]" />
              )}
              <span className="text-foreground text-[13px] leading-4">
                {deal.source.label}
              </span>
            </div>
          ) : (
            <span className="text-[#7A746B] text-[13px] leading-4">
              —
            </span>
          )}
        </PropertyRow>
        <Divider />
        <PropertyRow label={t`Created`}>
          <span className="text-foreground text-[13px] leading-4">
            {deal.created.at} by {deal.created.by}
          </span>
        </PropertyRow>
      </div>
    </section>
  )
}

function ValueRow({
  deal,
  dealPropKey,
}: {
  deal: DealDrawerData
  dealPropKey: DealPropKey
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(deal.value ?? "")
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Keep the draft in sync when the prop changes (real-time
  // broadcast, optimistic revert, fresh page render). We
  // don't overwrite the user's keystrokes while they're
  // editing — only when the row is idle.
  useEffect(() => {
    if (!editing) {
      setDraft(deal.value ?? "")
    }
  }, [deal.value, editing])

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [editing])

  const { t } = useLingui()

  const { submitting, submit } = useDealUpdate({
    deal,
    dealPropKey,
    onSuccess: () => {
      setEditing(false)
      setError(null)
    },
    onError: (errors) => {
      setError(
        errors.value ??
          errors.base ??
          t`Couldn't save the value. Try again.`,
      )
    },
  })

  const cancel = useCallback(() => {
    setDraft(deal.value ?? "")
    setEditing(false)
    setError(null)
  }, [deal.value])

  const commit = useCallback(() => {
    const trimmed = draft.trim()
    if (trimmed === "") {
      submit({
        optimisticPatch: { value: null },
        payload: { value: "" },
      })
      return
    }

    const sanitized = trimmed.replace(/[^\d.\-]/g, "")
    if (sanitized === "") {
      setError(t`That doesn't look like a number.`)
      return
    }
    const amount = Number(sanitized)
    if (Number.isNaN(amount)) {
      setError(t`That doesn't look like a number.`)
      return
    }
    if (amount < 0) {
      setError(t`Value can't be negative.`)
      return
    }

    submit({
      optimisticPatch: { value: formatCurrencyDisplay(amount) },
      payload: { value: trimmed },
    })
  }, [draft, submit])

  if (!editing) {
    return (
      <PropertyRow label={t`Value`}>
        <EditableTrigger
          ariaLabel={t`Edit value`}
          onClick={() => setEditing(true)}
        >
          <span className="font-semibold text-foreground text-[13px] leading-4 underline-offset-4 [text-decoration:underline_1px_dotted_#C9C2B6]">
            {deal.value ?? "—"}
          </span>
        </EditableTrigger>
      </PropertyRow>
    )
  }

  return (
    <EditingRow label={t`Value`}>
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            commit()
          } else if (e.key === "Escape") {
            e.preventDefault()
            e.stopPropagation()
            cancel()
          }
        }}
        aria-label={t`Deal value`}
        aria-invalid={Boolean(error)}
        aria-errormessage={error ? "value-error" : undefined}
        disabled={submitting}
        placeholder="0"
        className="min-w-[110px] rounded-md py-1 px-2 bg-white border border-[#B86A2D] text-foreground text-[13px] leading-4 font-medium focus:outline-none [box-shadow:#B86A2D1F_0px_0px_0px_3px]"
      />
      <CommitButton onClick={commit} disabled={submitting} />
      <CancelButton onClick={cancel} disabled={submitting} />
      {error ? (
        <span
          id="value-error"
          role="alert"
          className="text-[#B6675F] text-xs leading-4"
        >
          {error}
        </span>
      ) : null}
    </EditingRow>
  )
}

function CloseDateRow({
  deal,
  dealPropKey,
}: {
  deal: DealDrawerData
  dealPropKey: DealPropKey
}) {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const { t } = useLingui()

  const { submitting, submit } = useDealUpdate({
    deal,
    dealPropKey,
    onSuccess: () => {
      setOpen(false)
      setError(null)
    },
    onError: (errors) => {
      setError(
        errors.close_date ??
          errors.base ??
          t`Couldn't save the date. Try again.`,
      )
    },
  })

  const apply = useCallback(
    (iso: string | null) => {
      submit({
        optimisticPatch: {
          closeAt: formatCloseAtDisplay(iso),
          closeDateIso: iso,
        },
        payload: { close_date: iso ?? "" },
      })
    },
    [submit],
  )

  return (
    <>
      <PropertyRow label={t`Close date`}>
        <EditableTrigger
          ariaLabel={t`Edit close date`}
          onClick={() => setOpen(true)}
          ref={triggerRef}
        >
          <span className="text-foreground text-[13px] leading-4 underline-offset-4 [text-decoration:underline_1px_dotted_#C9C2B6]">
            {deal.closeAt ?? "—"}
          </span>
        </EditableTrigger>
      </PropertyRow>
      {open && triggerRef.current ? (
        <DatePopover
          anchor={triggerRef.current}
          selectedIso={deal.closeDateIso}
          submitting={submitting}
          error={error}
          ariaLabel={t`Pick close date`}
          presets={CLOSE_DATE_PRESETS(t)}
          onClose={() => {
            setOpen(false)
            setError(null)
          }}
          onApply={apply}
        />
      ) : null}
    </>
  )
}

// Quick-pick presets for the deal close-date popover. Built
// per render off `new Date()` so the dates track the real
// today instead of baking in the module-load day. Mirrors the
// presets the local DatePopover shipped before the extraction
// (Today / In 1 week / End of month / Clear).
function CLOSE_DATE_PRESETS(t: ReturnType<typeof useLingui>["t"]): DatePopoverPreset[] {
  const today = new Date()
  return [
    { label: t(msg`Today`), iso: isoFromDate(today) },
    { label: t(msg`In 1 week`), iso: isoFromDate(addDays(today, 7)) },
    { label: t(msg`End of month`), iso: isoFromDate(endOfMonth(today)) },
    { label: t(msg`Clear`), iso: null },
  ]
}

// Owner row — a deal can have many owners (CON-218). Shows
// the overlapping avatar stack + "David +1" label and opens
// the rich multi-select picker. The single-select
// `OwnerPopover` is intentionally NOT used here anymore —
// it still powers the next-step assignee reassign.
function OwnerRow({
  deal,
  dealPropKey,
}: {
  deal: DealDrawerData
  dealPropKey: DealPropKey
}) {
  const { t } = useLingui()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const owners = dealOwnersList(deal)

  return (
    <>
      <PropertyRow label={t`Owner`}>
        <EditableTrigger
          ariaLabel={t`Edit owners`}
          onClick={() => setOpen(true)}
          ref={triggerRef}
        >
          {owners.length > 0 ? (
            <div className="flex items-center gap-1.5 min-w-0">
              <AvatarStack people={owners} size={20} />
              <span className="text-foreground text-[13px] leading-4 truncate underline-offset-4 [text-decoration:underline_1px_dotted_#C9C2B6]">
                {stackLabel(owners)}
              </span>
            </div>
          ) : (
            <span className="text-[#7A746B] text-[13px] leading-4 underline-offset-4 [text-decoration:underline_1px_dotted_#C9C2B6]">
              <Trans>Unassigned</Trans>
            </span>
          )}
        </EditableTrigger>
      </PropertyRow>
      <DealOwnersPicker
        deal={deal}
        dealPropKey={dealPropKey}
        open={open}
        onClose={() => setOpen(false)}
        triggerRef={triggerRef}
      />
    </>
  )
}

// Trigger button used by editable rows. Wraps the value
// in a button so click + keyboard activation both open
// the editor. Forwards a ref so the popovers can anchor
// to the trigger.
const EditableTrigger = forwardRef<
  HTMLButtonElement,
  {
    children: ReactNode
    onClick: () => void
    ariaLabel: string
  }
>(function EditableTrigger(
  { children, onClick, ariaLabel },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className="flex items-center gap-1.5 cursor-pointer text-left"
    >
      {children}
    </button>
  )
})

// Editing-state row: warm tint background, 2px orange
// left stripe per Paper Q1L-1.
function EditingRow({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div
      className="flex items-center py-[9px] pl-3.5 pr-4 gap-3 bg-[#FAF7F1] border-l-2 border-l-[#B86A2D]"
      data-state="editing"
    >
      <span className="grow text-[#7A746B] text-[13px] leading-4">
        {label}
      </span>
      <div className="flex items-center gap-1.5">{children}</div>
    </div>
  )
}

function CommitButton({
  onClick,
  disabled,
}: {
  onClick: () => void
  disabled?: boolean
}) {
  const { t } = useLingui()
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={t`Save`}
      className="flex items-center justify-center w-[26px] h-[26px] rounded-md shrink-0 bg-[#1E1B17] cursor-pointer disabled:opacity-60"
    >
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M3 8L6.5 11.5L13 4.5"
          stroke="#F0EDE6"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  )
}

function CancelButton({
  onClick,
  disabled,
}: {
  onClick: () => void
  disabled?: boolean
}) {
  const { t } = useLingui()
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={t`Cancel`}
      className="flex items-center justify-center w-[26px] h-[26px] rounded-md shrink-0 bg-white border border-[#C9C2B6] cursor-pointer disabled:opacity-60"
    >
      <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M4 4L12 12M12 4L4 12"
          stroke="#7A746B"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
    </button>
  )
}

function PropertyRow({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex items-center py-3 px-4 gap-3">
      <span className="grow text-[#7A746B] text-[13px] leading-4">
        {label}
      </span>
      <div className="flex items-center gap-1.5">{children}</div>
    </div>
  )
}

function Divider() {
  return <div className="h-px ml-4 bg-[#F0EDE6]" />
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <h2 className="tracking-[0.06em] uppercase font-semibold text-[#7A746B] text-[11px] leading-3">
      {children}
    </h2>
  )
}

function WhatsAppMark() {
  return (
    <span className="flex items-center justify-center w-[14px] h-[14px] shrink-0">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <circle cx="7" cy="7" r="7" fill="#25D366" />
        <path
          d="M9.7 8.4c-.1 0-.7-.4-.8-.4-.1-.1-.2-.1-.3 0-.1.1-.4.4-.5.5-.1.1-.2.1-.3 0-.4-.2-.8-.4-1.2-.9-.3-.3-.5-.7-.7-1.2-.1-.1 0-.2.1-.3l.4-.4c.1-.1.1-.2.1-.3 0 0-.6-1.4-.6-1.5 0-.1-.1-.1-.2-.1H4.5c-.1 0-.2 0-.3.1-.4.4-.6.9-.6 1.4 0 1 .8 2 1 2.1 1.3 1.7 2.5 2.1 2.9 2.2.3.1.6.1.9.1.3 0 .6-.1.8-.2.2-.1.5-.4.6-.6.1-.2.1-.4.1-.6 0 0-.1-.1-.2-.2z"
          fill="white"
        />
      </svg>
    </span>
  )
}

